{
  "openapi": "3.1.0",
  "info": {
    "title": "power-up API",
    "version": "1.0.0",
    "description": "API specification for the power-up service"
  },
  "paths": {
    "/parts": {
      "get": {
        "summary": "Get a list of parts with pagination",
        "operationId": "listParts",
        "parameters": [
          {
            "name": "limit",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "minimum": 1,
              "maximum": 10000,
              "default": 1000
            },
            "description": "Maximum number of parts to return"
          },
          {
            "name": "offset",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "minimum": 0,
              "default": 0
            },
            "description": "Number of parts to skip"
          }
        ],
        "responses": {
          "200": {
            "description": "Paginated parts. For admin users, `organizations` is included with every organization referenced by `org_id` on the returned parts. Non-admin responses omit `organizations` and do not expose `org_id` on each part.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "required": [
                    "parts"
                  ],
                  "properties": {
                    "parts": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/part"
                      }
                    },
                    "organizations": {
                      "type": "array",
                      "description": "Included for admin users only: organizations referenced by `org_id` on the returned parts.\n",
                      "items": {
                        "$ref": "#/components/schemas/organization"
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      },
      "post": {
        "summary": "Create a new part",
        "operationId": "createPart",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/part-request"
              }
            }
          }
        },
        "responses": {
          "201": {
            "description": "Part created successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "required": [
                    "part"
                  ],
                  "properties": {
                    "part": {
                      "$ref": "#/components/schemas/part"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user does not have required privileges (admin only).",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "422": {
            "description": "Unprocessable entity — `org_id` references an organization that does not exist.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/parts/listed": {
      "get": {
        "summary": "List parts for the current user's organization",
        "operationId": "listedParts",
        "description": "Returns marketplace parts assigned to the organization whose `creator_id` matches the authenticated user. When the user has no organization, the response is an empty `parts` array. Each part follows the `Part` schema but `org_id` is never included (same rule as non-admin callers elsewhere).\n",
        "responses": {
          "200": {
            "description": "Parts for the user's organization, or an empty list when they have no organization",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "parts": {
                      "type": "array",
                      "description": "Parts whose `org_id` matches the caller's organization. Empty when the user has no organization. `org_id` is omitted from every item; `sold_off_platform` and all other part fields are included as defined on `Part`.\n",
                      "items": {
                        "$ref": "#/components/schemas/part"
                      }
                    }
                  },
                  "required": [
                    "parts"
                  ]
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/parts/{id}": {
      "get": {
        "summary": "Get a specific part by ID",
        "operationId": "getPartById",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string",
              "minLength": 1
            },
            "description": "Unique identifier of the part (short id)"
          }
        ],
        "responses": {
          "200": {
            "description": "Part details. For admin users, `organization` is included (null when the part has no organization). Non-admin responses omit `organization` and do not expose `org_id` on `part`.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "required": [
                    "part"
                  ],
                  "properties": {
                    "part": {
                      "$ref": "#/components/schemas/part"
                    },
                    "organization": {
                      "description": "Included for admin users only: the organization for `part.org_id`, or null when unassigned.\n",
                      "oneOf": [
                        {
                          "$ref": "#/components/schemas/organization"
                        },
                        {
                          "enum": [
                            null
                          ]
                        }
                      ]
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Part not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      },
      "put": {
        "summary": "Update an existing part",
        "operationId": "updatePart",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string",
              "minLength": 1
            },
            "description": "Unique identifier of the part (short id)"
          }
        ],
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/part-request"
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Part updated successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "required": [
                    "part"
                  ],
                  "properties": {
                    "part": {
                      "$ref": "#/components/schemas/part"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user does not have required privileges (admin only).",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Part not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "422": {
            "description": "Unprocessable entity — `org_id` references an organization that does not exist.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      },
      "delete": {
        "summary": "Delete a part",
        "operationId": "deletePart",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string",
              "minLength": 1
            },
            "description": "Unique identifier of the part (short id)"
          }
        ],
        "responses": {
          "200": {
            "description": "Part deleted successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "success": {
                      "type": "boolean",
                      "example": true
                    },
                    "message": {
                      "type": "string",
                      "example": "Part deleted successfully"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user does not have required privileges (admin only).",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Part not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/parts/{id}/delist": {
      "post": {
        "summary": "Delist a part owned by the current user's organization",
        "operationId": "delistPart",
        "description": "Sets the part's status to `removed` when it belongs to the caller's organization and is currently `available`. Updates `sold_off_platform` from the request body. Sends an admin notification email. The returned `part` omits `org_id` (non-admin shape).\n",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string",
              "minLength": 1
            },
            "description": "Unique identifier of the part (short id)"
          }
        ],
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "sold_off_platform": {
                    "type": "boolean",
                    "description": "Whether the part was sold outside the marketplace before delisting; persisted on the part record.\n"
                  }
                },
                "required": [
                  "sold_off_platform"
                ]
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Part delisted successfully; status is `removed` and `sold_off_platform` is updated",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "required": [
                    "part"
                  ],
                  "properties": {
                    "part": {
                      "$ref": "#/components/schemas/part"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - part is not assigned to the caller's organization",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Part not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "409": {
            "description": "Conflict - the part's status is not `available`; only available parts can be delisted through this endpoint\n",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/parts/{id}/file": {
      "post": {
        "summary": "Upload a file for a part",
        "description": "Upload a file associated with a specific part in the Azure blob storage system. Supports multiple file categories for different types of documentation and reports including pedigree certificates, shop releases, flow test reports, dimensional inspections, and NDE reports.",
        "operationId": "uploadPartFile",
        "tags": [],
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "The ID of the part to upload a file for",
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "required": true,
          "content": {
            "multipart/form-data": {
              "schema": {
                "type": "object",
                "required": [
                  "file",
                  "category"
                ],
                "properties": {
                  "file": {
                    "type": "string",
                    "format": "binary",
                    "description": "The file to upload"
                  },
                  "category": {
                    "$ref": "#/components/schemas/category"
                  },
                  "force": {
                    "type": "boolean",
                    "default": false,
                    "description": "Whether to overwrite existing files with the same name"
                  }
                }
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "File uploaded successfully",
            "content": {
              "application/json": {
                "schema": {
                  "allOf": [
                    {
                      "$ref": "#/components/schemas/part-file"
                    },
                    {
                      "type": "object",
                      "properties": {
                        "message": {
                          "type": "string",
                          "example": "File uploaded successfully"
                        }
                      }
                    }
                  ]
                }
              }
            }
          },
          "400": {
            "description": "Bad request - invalid file category or missing required fields",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "code": {
                      "type": "string",
                      "enum": [
                        "INVALID_FILE_TYPE"
                      ]
                    },
                    "message": {
                      "type": "string"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - authentication required",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - admin privileges required",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Part not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "409": {
            "description": "Conflict - file naming conflict when force=false",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "500": {
            "description": "Internal server error",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/parts/{id}/files": {
      "get": {
        "summary": "Get files for a part",
        "description": "Retrieve all files associated with a specific part, including metadata such as category, original name, MIME type, size, and creation timestamp.",
        "operationId": "getPartFiles",
        "tags": [],
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "The ID of the part to get files for",
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Files retrieved successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "array",
                  "items": {
                    "$ref": "#/components/schemas/part-file"
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - authentication required",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Part not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/parts/{id}/files/{fileId}": {
      "delete": {
        "summary": "Delete a file for a part",
        "description": "Delete a specific file associated with a part. Requires admin privileges to perform this operation.",
        "operationId": "deletePartFile",
        "tags": [],
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "The ID of the part that owns the file",
            "schema": {
              "type": "string"
            }
          },
          {
            "name": "fileId",
            "in": "path",
            "required": true,
            "description": "The ID of the file to delete",
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "description": "File deleted successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "message": {
                      "type": "string",
                      "example": "File deleted successfully"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - authentication required",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - admin privileges required",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Part or file not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/parts/download": {
      "get": {
        "summary": "Download parts data as CSV",
        "operationId": "downloadParts",
        "responses": {
          "200": {
            "description": "CSV file containing parts data",
            "content": {
              "text/csv": {
                "schema": {
                  "type": "string",
                  "format": "binary"
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user does not have required privileges.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/organization": {
      "get": {
        "summary": "Get current user's organization",
        "operationId": "getOrganization",
        "responses": {
          "200": {
            "description": "Organization retrieved successfully",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/organization"
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Organization not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      },
      "post": {
        "summary": "Create a new organization for the current user",
        "operationId": "createOrganization",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/organization-request"
              }
            }
          }
        },
        "responses": {
          "201": {
            "description": "Organization created successfully",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/organization"
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden — CSRF failure or insufficient privileges.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "409": {
            "description": "Conflict - organization already exists for this user",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      },
      "put": {
        "summary": "Update the current user's organization",
        "operationId": "updateOrganization",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/organization-request"
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Organization updated successfully",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/organization"
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden — CSRF failure or insufficient privileges.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Organization not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/organizations": {
      "get": {
        "summary": "List all organizations with creator user info",
        "operationId": "listOrganizations",
        "description": "Returns every organization in the system together with Kratos-resolved user records for each organization's `creator_id`. Admin access required.\n",
        "responses": {
          "200": {
            "description": "Organizations and their creator users retrieved successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "organizations": {
                      "type": "array",
                      "description": "All organizations, in no particular order.",
                      "items": {
                        "$ref": "#/components/schemas/organization"
                      }
                    },
                    "users": {
                      "type": "array",
                      "description": "Distinct users matching `creator_id` on the returned organizations, resolved from Kratos admin API (one entry per creator identity).\n",
                      "items": {
                        "$ref": "#/components/schemas/user"
                      }
                    }
                  },
                  "required": [
                    "organizations",
                    "users"
                  ]
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user does not have required admin privileges",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/organizations/{id}": {
      "get": {
        "summary": "Get an organization's name by id",
        "operationId": "getOrganizationById",
        "description": "Returns the organization name when the id is the caller's own organization. Does not return contact details, address, or creator. Other organizations respond as not found so marketplace callers cannot look up a seller.\n",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "Organization id (short id).",
            "schema": {
              "type": "string",
              "minLength": 1
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Name of the caller's organization",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "id",
                    "name"
                  ],
                  "properties": {
                    "id": {
                      "type": "string",
                      "description": "Organization id."
                    },
                    "name": {
                      "type": "string",
                      "description": "Company name. No other organization fields are included."
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Organization not found, or it is not the caller's organization",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/users/{id}": {
      "get": {
        "summary": "Get a person's name by id",
        "operationId": "getUserById",
        "description": "Returns the person's name when they belong to the caller's organization (the organization creator, the caller, or someone who submitted a repair for that organization). Email, phone, and other contact fields are never returned. Anyone outside the organization responds as not found.\n",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "Kratos identity id.",
            "schema": {
              "type": "string",
              "minLength": 1
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Name of a person in the caller's organization",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "id"
                  ],
                  "properties": {
                    "id": {
                      "type": "string",
                      "description": "Kratos identity id."
                    },
                    "name": {
                      "type": "object",
                      "nullable": true,
                      "additionalProperties": false,
                      "description": "Given and family name. Null when the identity has no name traits.\n",
                      "required": [
                        "first",
                        "last"
                      ],
                      "properties": {
                        "first": {
                          "type": "string"
                        },
                        "last": {
                          "type": "string"
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "User not found in the caller's organization",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/organization/logo": {
      "post": {
        "summary": "Upload a logo for the organization",
        "description": "Upload a logo file for the organization in the Azure blob storage system. The logo will be associated with the authenticated user's organization.",
        "operationId": "uploadLogoOrganization",
        "tags": [],
        "requestBody": {
          "required": true,
          "content": {
            "multipart/form-data": {
              "schema": {
                "type": "object",
                "required": [
                  "file"
                ],
                "properties": {
                  "file": {
                    "type": "string",
                    "format": "binary",
                    "description": "The logo file to upload"
                  }
                }
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Logo uploaded successfully",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/organization"
                }
              }
            }
          },
          "400": {
            "description": "Bad request - invalid file type",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - authentication required",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden — CSRF failure or insufficient privileges.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Organization not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "413": {
            "description": "Payload too large - file size exceeds maximum allowed",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/messages/buy-interest": {
      "post": {
        "summary": "Submit a buy-interest request for a part",
        "operationId": "submitBuyInterest",
        "description": "Submit a request expressing interest in a specific part and requesting certain file categories",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/part-interest-request"
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Buy-interest request submitted successfully",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/part-interest-response"
                }
              }
            }
          },
          "400": {
            "description": "Bad request - missing required fields or company profile not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Part not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/messages/sell-interest": {
      "post": {
        "tags": [
          "email-verified"
        ],
        "summary": "Submit a request to sell turbine part(s)",
        "operationId": "submitSellInterest",
        "description": "Submit seller account details and part description; staff are notified by email.",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/sell-interest-request"
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Sell-interest request submitted successfully",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/sell-interest-response"
                }
              }
            }
          },
          "400": {
            "description": "Bad request — invalid or incomplete payload",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized — missing or invalid auth",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden — email not verified",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/messages/contact": {
      "post": {
        "tags": [
          "no-auth"
        ],
        "summary": "Submit public contact form",
        "operationId": "submitContactMessage",
        "description": "Accepts a contact message from the public marketing site. Does not require authentication. The server forwards the message to staff.\n",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/contact-request"
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Message recorded successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "success": {
                      "type": "boolean",
                      "description": "Always true when the handler completes without error.",
                      "example": true
                    }
                  },
                  "required": [
                    "success"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "Bad request — invalid or missing required fields",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/messages/delist-feedback": {
      "post": {
        "summary": "Submit feedback after delisting a part",
        "operationId": "submitDelistFeedback",
        "description": "Accepts free-form feedback from an authenticated user, associated with a part they recently interacted with (typically after delisting). The server may forward the message to staff.\n",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "part_id": {
                    "type": "string",
                    "minLength": 1,
                    "description": "Identifier of the part this feedback refers to (short id, same format as part URLs).",
                    "example": "xY3nK9pL"
                  },
                  "message": {
                    "type": "string",
                    "maxLength": 2000,
                    "description": "Free-form feedback text (maximum 2000 characters).",
                    "example": "The delist flow was clear; it would help to see a confirmation email sooner."
                  }
                },
                "required": [
                  "part_id",
                  "message"
                ]
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Feedback recorded successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "success": {
                      "type": "boolean",
                      "description": "Always true when the handler completes without error.",
                      "example": true
                    }
                  },
                  "required": [
                    "success"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "Bad request — invalid or empty part_id or message",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/messages/support-feedback": {
      "post": {
        "tags": [
          "email-verified"
        ],
        "summary": "Submit account support feedback",
        "operationId": "submitSupportFeedback",
        "description": "Authenticated user sends support feedback from the account app. The server emails staff; the email subject is taken from the request body subject field.\n",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/support-feedback-request"
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Feedback recorded; staff will be notified by email",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "success": {
                      "type": "boolean",
                      "description": "Always true when the handler completes without error.",
                      "example": true
                    }
                  },
                  "required": [
                    "success"
                  ]
                }
              }
            }
          },
          "400": {
            "description": "Bad request — invalid or missing required fields",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized — missing or invalid auth",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/part-sources": {
      "get": {
        "summary": "List all part sources",
        "operationId": "listPartSource",
        "description": "Retrieves a list of all part sources. Admin access required.",
        "responses": {
          "200": {
            "description": "List of part sources retrieved successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "sources": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/part-source"
                      },
                      "description": "Array of part source objects"
                    }
                  },
                  "required": [
                    "sources"
                  ]
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user does not have required admin privileges",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      },
      "post": {
        "summary": "Upload CSV file to create part source and import parts",
        "operationId": "uploadCsvPartSource",
        "description": "Accepts a CSV file upload to create a new part source and import parts. Admin access required.",
        "requestBody": {
          "required": true,
          "content": {
            "multipart/form-data": {
              "schema": {
                "type": "object",
                "properties": {
                  "file": {
                    "type": "string",
                    "format": "binary",
                    "description": "CSV file containing part data"
                  }
                },
                "required": [
                  "file"
                ]
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "CSV file uploaded and processed successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "id": {
                      "type": "string",
                      "format": "uuid",
                      "description": "Unique identifier for the uploaded part source",
                      "example": "123e4567-e89b-12d3-a456-426614174000"
                    },
                    "message": {
                      "type": "string",
                      "description": "Success message describing the upload result",
                      "example": "CSV file uploaded and processed successfully"
                    },
                    "parts_created": {
                      "type": "integer",
                      "minimum": 0,
                      "description": "Number of parts created from the CSV file",
                      "example": 150
                    }
                  },
                  "required": [
                    "id",
                    "message",
                    "parts_created"
                  ]
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user does not have required admin privileges",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "500": {
            "description": "Internal server error during CSV processing",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/part-sources/{id}": {
      "get": {
        "summary": "Get a part source by ID",
        "operationId": "getByIdPartSource",
        "description": "Retrieves a part source by its unique identifier. Admin access required.",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string",
              "format": "uuid"
            },
            "description": "Unique identifier of the part source to retrieve",
            "example": "123e4567-e89b-12d3-a456-426614174000"
          }
        ],
        "responses": {
          "200": {
            "description": "Part source retrieved successfully",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/part-source"
                }
              }
            }
          },
          "400": {
            "description": "Bad request - missing or invalid ID parameter",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user does not have required admin privileges",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Part source not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      },
      "delete": {
        "summary": "Delete a part source and all associated parts",
        "operationId": "removePartSource",
        "description": "Deletes a part source by ID and removes all parts created from that source. Admin access required.",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "schema": {
              "type": "string",
              "format": "uuid"
            },
            "description": "Unique identifier of the part source to delete",
            "example": "123e4567-e89b-12d3-a456-426614174000"
          }
        ],
        "responses": {
          "200": {
            "description": "Part source and associated parts deleted successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "message": {
                      "type": "string",
                      "description": "Success message describing the deletion result",
                      "example": "Part source and 150 associated parts deleted successfully"
                    },
                    "parts_deleted": {
                      "type": "integer",
                      "minimum": 0,
                      "description": "Number of parts deleted along with the source",
                      "example": 150
                    }
                  },
                  "required": [
                    "message",
                    "parts_deleted"
                  ]
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user does not have required admin privileges",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Part source not found",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/system/backup": {
      "post": {
        "summary": "Trigger system backup process",
        "operationId": "backupSystem",
        "responses": {
          "204": {
            "description": "Backup process initiated successfully"
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user does not have required privileges.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/mercury-transactions/sync": {
      "post": {
        "summary": "Sync Mercury transactions into the local mirror",
        "description": "Pull new and updated transactions from Mercury for the configured account and upsert into `mercury_transaction`. Shared domain implementation with the cron job. Does not modify allocations.\n",
        "operationId": "syncMercuryTransactions",
        "tags": [
          "site-admin-only",
          "background"
        ],
        "responses": {
          "200": {
            "description": "Sync completed; returns counts and the sync timestamp.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "sync"
                  ],
                  "properties": {
                    "sync": {
                      "type": "object",
                      "additionalProperties": false,
                      "description": "Result of a Mercury transaction sync run.",
                      "required": [
                        "upserted",
                        "scanned",
                        "synced_at"
                      ],
                      "properties": {
                        "upserted": {
                          "type": "integer",
                          "minimum": 0,
                          "description": "Number of transactions inserted or updated.",
                          "example": 12
                        },
                        "scanned": {
                          "type": "integer",
                          "minimum": 0,
                          "description": "Number of Mercury transactions examined this run.",
                          "example": 40
                        },
                        "synced_at": {
                          "type": "string",
                          "format": "date-time",
                          "description": "When this sync run finished (ISO 8601).",
                          "example": "2026-09-21T18:30:00.000Z"
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "502": {
            "description": "Bad Gateway - Mercury upstream request failed.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/admin/dashboard-summary": {
      "get": {
        "summary": "Get admin home dashboard summary counts",
        "description": "Returns outstanding Mercury transaction counts, unallocated remainder totals, and open repair request counts for the admin home dashboard.\n",
        "operationId": "dashboardSummaryAdmin",
        "tags": [
          "site-admin-only"
        ],
        "responses": {
          "200": {
            "description": "Dashboard summary counts retrieved successfully.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "dashboardSummary"
                  ],
                  "properties": {
                    "dashboardSummary": {
                      "$ref": "#/components/schemas/admin-dashboard-summary"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/mercury-transactions": {
      "get": {
        "summary": "List Mercury transactions",
        "description": "Admin list of mirrored Mercury bank transactions with optional outstanding and posted-date filters, plus offset pagination. Ordered by `posted_at` descending (then `id`). Includes computed `allocated_amount_cents` and `outstanding` on each row.\n",
        "operationId": "listMercuryTransactions",
        "tags": [
          "site-admin-only"
        ],
        "parameters": [
          {
            "name": "outstanding",
            "in": "query",
            "required": false,
            "description": "When true, only incompletely tagged transactions; when false, only fully tagged. Omit to return both.\n",
            "schema": {
              "$ref": "#/components/schemas/outstanding"
            }
          },
          {
            "name": "posted_after",
            "in": "query",
            "required": false,
            "description": "Only include transactions with `posted_at` greater than or equal to this instant.",
            "schema": {
              "$ref": "#/components/schemas/posted_at"
            }
          },
          {
            "name": "posted_before",
            "in": "query",
            "required": false,
            "description": "Only include transactions with `posted_at` less than or equal to this instant.",
            "schema": {
              "$ref": "#/components/schemas/posted_at"
            }
          },
          {
            "name": "limit",
            "in": "query",
            "required": false,
            "description": "Page size (default 50).",
            "schema": {
              "type": "integer",
              "minimum": 1,
              "default": 50
            }
          },
          {
            "name": "offset",
            "in": "query",
            "required": false,
            "description": "Number of matching transactions to skip before returning results.",
            "schema": {
              "type": "integer",
              "minimum": 0,
              "default": 0
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Matching Mercury transactions for this page.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "mercuryTransactions"
                  ],
                  "properties": {
                    "mercuryTransactions": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/mercury-transaction"
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/mercury-transactions/{id}": {
      "get": {
        "summary": "Get a Mercury transaction by ID",
        "description": "Admin detail for a single mirrored Mercury bank transaction, including its allocations and the organizations and parts those allocations reference (empty arrays when none).\n",
        "operationId": "getByIdMercuryTransactions",
        "tags": [
          "site-admin-only"
        ],
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "Local unique identifier of the Mercury transaction.",
            "schema": {
              "$ref": "#/components/schemas/id"
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Transaction detail with sibling allocations, organizations, and parts referenced by those allocations.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "mercuryTransaction",
                    "mercuryTransactionAllocations",
                    "organizations",
                    "parts"
                  ],
                  "properties": {
                    "mercuryTransaction": {
                      "$ref": "#/components/schemas/mercury-transaction"
                    },
                    "mercuryTransactionAllocations": {
                      "type": "array",
                      "description": "Allocations that split this transaction (empty when none).",
                      "items": {
                        "$ref": "#/components/schemas/mercury-transaction-allocation"
                      }
                    },
                    "organizations": {
                      "type": "array",
                      "description": "Organizations referenced by `org_id` on the returned allocations (empty when none).\n",
                      "items": {
                        "$ref": "#/components/schemas/organization"
                      }
                    },
                    "parts": {
                      "type": "array",
                      "description": "Parts referenced by `part_id` on the returned allocations (empty when none).\n",
                      "items": {
                        "$ref": "#/components/schemas/part"
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Mercury transaction not found.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/mercury-transaction-allocations/by-transaction-ids": {
      "get": {
        "summary": "Batch fetch allocations by transaction IDs",
        "description": "Admin batch lookup of Mercury transaction allocations for many parent transactions (list UIs). Returns allocations grouped by local transaction id. Each requested id is present in the map (empty array when that transaction has no allocations).\n",
        "operationId": "byTransactionIdsMercuryTransactionAllocations",
        "tags": [
          "site-admin-only"
        ],
        "parameters": [
          {
            "name": "transactionIds",
            "in": "query",
            "required": true,
            "description": "Comma-separated local Mercury transaction ids to fetch allocations for.\n",
            "style": "form",
            "explode": false,
            "schema": {
              "type": "array",
              "items": {
                "$ref": "#/components/schemas/id"
              }
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Allocations keyed by Mercury transaction id. Every requested id is a key; values are empty arrays when there are no allocations.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "mercuryTransactionAllocationsByTransactionId"
                  ],
                  "properties": {
                    "mercuryTransactionAllocationsByTransactionId": {
                      "type": "object",
                      "description": "Map from local Mercury transaction id to that transaction's allocations.\n",
                      "additionalProperties": {
                        "type": "array",
                        "items": {
                          "$ref": "#/components/schemas/mercury-transaction-allocation"
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "422": {
            "description": "Unprocessable entity — one or more `transactionIds` do not reference an existing Mercury transaction.\n",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/mercury-transaction-allocations": {
      "post": {
        "summary": "Create a Mercury transaction allocation",
        "description": "Admin create of one allocation (partial or full split) on a mirrored Mercury transaction. Sets `tagged_by_user_id` from the authenticated admin. Returns the new allocation and the parent transaction with recomputed `allocated_amount_cents` / `outstanding`.\n",
        "operationId": "createMercuryTransactionAllocations",
        "tags": [
          "site-admin-only"
        ],
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "additionalProperties": false,
                "required": [
                  "mercury_transaction_id",
                  "kind",
                  "amount_cents"
                ],
                "properties": {
                  "mercury_transaction_id": {
                    "$ref": "#/components/schemas/mercury_transaction_id"
                  },
                  "org_id": {
                    "$ref": "#/components/schemas/org_id"
                  },
                  "kind": {
                    "$ref": "#/components/schemas/kind"
                  },
                  "part_id": {
                    "$ref": "#/components/schemas/part_id"
                  },
                  "repair_id": {
                    "$ref": "#/components/schemas/repair_id"
                  },
                  "amount_cents": {
                    "$ref": "#/components/schemas/amount_cents"
                  },
                  "note": {
                    "$ref": "#/components/schemas/note"
                  }
                }
              }
            }
          }
        },
        "responses": {
          "201": {
            "description": "Allocation created. Includes the updated parent transaction so clients can refresh outstanding state.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "mercuryTransactionAllocation",
                    "mercuryTransaction"
                  ],
                  "properties": {
                    "mercuryTransactionAllocation": {
                      "$ref": "#/components/schemas/mercury-transaction-allocation"
                    },
                    "mercuryTransaction": {
                      "$ref": "#/components/schemas/mercury-transaction"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Parent Mercury transaction not found.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "409": {
            "description": "Conflict — allocation would over-allocate the parent or has the wrong sign relative to the parent amount.\n",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "422": {
            "description": "Unprocessable entity — kind rules violated (e.g. missing required org/part/repair for kind), or `org_id` / `part_id` / `repair_id` does not reference an existing resource.\n",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/mercury-transaction-allocations/{id}": {
      "put": {
        "summary": "Update a Mercury transaction allocation",
        "description": "Admin update of an allocation’s mutable fields / amount. Does not change `mercury_transaction_id`. Sets `tagged_by_user_id` from the authenticated admin. Returns the updated allocation and the parent transaction with recomputed `allocated_amount_cents` / `outstanding`.\n",
        "operationId": "updateMercuryTransactionAllocations",
        "tags": [
          "site-admin-only"
        ],
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "Unique identifier of the allocation to update.",
            "schema": {
              "$ref": "#/components/schemas/properties-id"
            }
          }
        ],
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "additionalProperties": false,
                "properties": {
                  "org_id": {
                    "$ref": "#/components/schemas/org_id"
                  },
                  "kind": {
                    "$ref": "#/components/schemas/kind"
                  },
                  "part_id": {
                    "$ref": "#/components/schemas/part_id"
                  },
                  "repair_id": {
                    "$ref": "#/components/schemas/repair_id"
                  },
                  "amount_cents": {
                    "$ref": "#/components/schemas/amount_cents"
                  },
                  "note": {
                    "$ref": "#/components/schemas/note"
                  }
                }
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Allocation updated. Includes the updated parent transaction so clients can refresh outstanding state.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "mercuryTransactionAllocation",
                    "mercuryTransaction"
                  ],
                  "properties": {
                    "mercuryTransactionAllocation": {
                      "$ref": "#/components/schemas/mercury-transaction-allocation"
                    },
                    "mercuryTransaction": {
                      "$ref": "#/components/schemas/mercury-transaction"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Allocation not found.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "409": {
            "description": "Conflict — allocation would over-allocate the parent or has the wrong sign relative to the parent amount.\n",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "422": {
            "description": "Unprocessable entity — kind rules violated (e.g. missing required org/part/repair for kind), or `org_id` / `part_id` / `repair_id` does not reference an existing resource.\n",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      },
      "delete": {
        "summary": "Delete a Mercury transaction allocation",
        "description": "Admin delete of an allocation. The parent transaction becomes outstanding again if remaining allocations no longer sum to its amount. Returns the parent transaction with recomputed `allocated_amount_cents` / `outstanding`.\n",
        "operationId": "deleteMercuryTransactionAllocations",
        "tags": [
          "site-admin-only"
        ],
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "Unique identifier of the allocation to delete.",
            "schema": {
              "$ref": "#/components/schemas/properties-id"
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Allocation deleted. Includes the updated parent transaction so clients can refresh outstanding state.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "success",
                    "mercuryTransaction"
                  ],
                  "properties": {
                    "success": {
                      "type": "boolean",
                      "description": "Always true when the handler completes without error.",
                      "example": true
                    },
                    "mercuryTransaction": {
                      "$ref": "#/components/schemas/mercury-transaction"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Allocation not found.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/credits/ledger": {
      "get": {
        "summary": "Get current user's org ledger balance and activity",
        "description": "Returns the Credit page ledger for the organization whose `creator_id` matches the authenticated user (same org scoping as `/parts/listed`). Includes balance summary, activity entries for seller-balance kinds (`part_sale`, `withdrawal`, `repair_spend`), and parts referenced by those entries. Excludes `platform_cut` and `test`. When the user has no organization, `orgLedgerSummary` is null and the arrays are empty.\n",
        "operationId": "ledgerCredits",
        "responses": {
          "200": {
            "description": "Org ledger summary, entries, and related parts — or null summary with empty arrays when the user has no organization.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "orgLedgerSummary",
                    "orgLedgerEntries",
                    "parts"
                  ],
                  "properties": {
                    "orgLedgerSummary": {
                      "description": "Balance summary for the caller's organization, or null when they have no organization.\n",
                      "oneOf": [
                        {
                          "$ref": "#/components/schemas/org-ledger-summary"
                        },
                        {
                          "enum": [
                            null
                          ]
                        }
                      ]
                    },
                    "orgLedgerEntries": {
                      "type": "array",
                      "description": "Flattened credit activity rows for the org (seller-balance kinds only). Empty when the user has no organization or no matching allocations. `running_balance_cents` may be omitted on this compact feed.\n",
                      "items": {
                        "$ref": "#/components/schemas/org-ledger-entry"
                      }
                    },
                    "parts": {
                      "type": "array",
                      "description": "Parts referenced by `part_id` on the returned entries (empty when none).\n",
                      "items": {
                        "$ref": "#/components/schemas/part"
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/credits/statement": {
      "get": {
        "summary": "Get printable full statement for current user's organization",
        "description": "Returns the complete credit statement for the organization whose `creator_id` matches the authenticated user (same org scoping as `/credits/ledger`). Entries are ordered oldest→newest with `running_balance_cents` populated on each row. Includes the organization header, balance summary, activity entries (seller-balance kinds: `part_sale`, `withdrawal`, `repair_spend`; excludes `platform_cut` and `test`), and referenced parts. Replaces `POST /messages/balance-statement`. When the user has no organization, `organization` and `orgLedgerSummary` are null and the arrays are empty; `generated_at` is always set.\n",
        "operationId": "statementCredits",
        "responses": {
          "200": {
            "description": "Full statement with organization, summary, entries (oldest→newest with running balances), related parts, and generation timestamp — or null org/summary with empty arrays when the user has no organization.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "organization",
                    "orgLedgerSummary",
                    "orgLedgerEntries",
                    "parts",
                    "generated_at"
                  ],
                  "properties": {
                    "organization": {
                      "description": "Caller's organization for the statement header, or null when they have no organization. May omit admin-only fields consistent with other non-admin org responses.\n",
                      "oneOf": [
                        {
                          "$ref": "#/components/schemas/organization"
                        },
                        {
                          "enum": [
                            null
                          ]
                        }
                      ]
                    },
                    "orgLedgerSummary": {
                      "description": "Balance summary for the caller's organization, or null when they have no organization.\n",
                      "oneOf": [
                        {
                          "$ref": "#/components/schemas/org-ledger-summary"
                        },
                        {
                          "enum": [
                            null
                          ]
                        }
                      ]
                    },
                    "orgLedgerEntries": {
                      "type": "array",
                      "description": "Complete credit activity history for the org (seller-balance kinds only), oldest→newest, with `running_balance_cents` set on each entry. Empty when the user has no organization or no matching allocations.\n",
                      "items": {
                        "$ref": "#/components/schemas/org-ledger-entry"
                      }
                    },
                    "parts": {
                      "type": "array",
                      "description": "Parts referenced by `part_id` on the returned entries (empty when none).\n",
                      "items": {
                        "$ref": "#/components/schemas/part"
                      }
                    },
                    "generated_at": {
                      "type": "string",
                      "format": "date-time",
                      "description": "Server time when this statement payload was generated (ISO 8601). Distinct from `orgLedgerSummary.as_of` so the print UI can show when the document was produced.\n",
                      "example": "2026-09-22T03:45:00.000Z"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/repairs": {
      "post": {
        "summary": "Create a repair request for the current user's organization",
        "description": "Authenticated user with an organization submits a repair request. Sets `created_by_user_id`, `org_id`, and `status` to `open`. Replaces `POST /messages/repair-credit-use`.\n",
        "operationId": "createRepairs",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "additionalProperties": false,
                "required": [
                  "part_to_repair",
                  "repair_cost_approx",
                  "repair_shop_name",
                  "work_order"
                ],
                "properties": {
                  "part_to_repair": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 2000,
                    "description": "Part the user wants repaired (free text)."
                  },
                  "repair_cost_approx": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 500,
                    "description": "Approximate repair cost as entered by the user (free text, not cents)."
                  },
                  "repair_shop_name": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 500,
                    "description": "Name of the repair shop."
                  },
                  "work_order": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 500,
                    "description": "Shop work-order or reference number."
                  },
                  "notes": {
                    "type": "string",
                    "maxLength": 5000,
                    "description": "Optional additional notes from the submitter."
                  }
                }
              }
            }
          }
        },
        "responses": {
          "201": {
            "description": "Repair created successfully",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "repair"
                  ],
                  "properties": {
                    "repair": {
                      "$ref": "#/components/schemas/repair"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Not found — authenticated user has no organization.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      },
      "get": {
        "summary": "List all repairs (admin)",
        "description": "Admin list of repair requests with optional `status` filter and offset pagination. Includes organizations referenced by `org_id` on the returned repairs.\n",
        "operationId": "listRepairs",
        "tags": [
          "site-admin-only"
        ],
        "parameters": [
          {
            "name": "status",
            "in": "query",
            "required": false,
            "description": "Filter by repair lifecycle status (`open` or `closed`). Omit to return both.",
            "schema": {
              "$ref": "#/components/schemas/status"
            }
          },
          {
            "name": "limit",
            "in": "query",
            "required": false,
            "description": "Page size (default 50).",
            "schema": {
              "type": "integer",
              "minimum": 1,
              "default": 50
            }
          },
          {
            "name": "offset",
            "in": "query",
            "required": false,
            "description": "Number of matching repairs to skip before returning results.",
            "schema": {
              "type": "integer",
              "minimum": 0,
              "default": 0
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Matching repairs for this page, with referenced organizations.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "repairs",
                    "organizations"
                  ],
                  "properties": {
                    "repairs": {
                      "type": "array",
                      "description": "Repair requests matching the query for this page.",
                      "items": {
                        "$ref": "#/components/schemas/repair"
                      }
                    },
                    "organizations": {
                      "type": "array",
                      "description": "Organizations referenced by `org_id` on the returned repairs.\n",
                      "items": {
                        "$ref": "#/components/schemas/organization"
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/repairs/mine": {
      "get": {
        "summary": "List repairs for the current user's organization",
        "description": "Returns repair requests for the organization of the authenticated user. Optional `status` filters to `open` or `closed`. When the user has no organization, the response is an empty `repairs` array.\n",
        "operationId": "mineRepairs",
        "parameters": [
          {
            "name": "status",
            "in": "query",
            "required": false,
            "description": "Filter by repair lifecycle status (`open` or `closed`). Omit to return both.",
            "schema": {
              "$ref": "#/components/schemas/status"
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Repairs for the user's organization, or an empty list when they have no organization.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "repairs"
                  ],
                  "properties": {
                    "repairs": {
                      "type": "array",
                      "description": "Repair requests for the caller's organization. Empty when the user has no organization or no matching repairs.\n",
                      "items": {
                        "$ref": "#/components/schemas/repair"
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/repairs/{id}": {
      "get": {
        "summary": "Get a repair by ID with status updates",
        "description": "Detail for a single repair request, including its status updates (empty array when none). Available to users in the owning organization or to admins.\n",
        "operationId": "getByIdRepairs",
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "Unique identifier of the repair (short id).",
            "schema": {
              "$ref": "#/components/schemas/repair_properties-id"
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Repair detail with sibling status updates (empty array when none).\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "repair",
                    "repairStatusUpdates"
                  ],
                  "properties": {
                    "repair": {
                      "$ref": "#/components/schemas/repair"
                    },
                    "repairStatusUpdates": {
                      "type": "array",
                      "description": "Status updates for this repair in chronological order (empty when none).\n",
                      "items": {
                        "$ref": "#/components/schemas/repair-status-update"
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - user is not in the owning organization and is not an admin.\n",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Repair not found.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      },
      "put": {
        "summary": "Update mutable free-text fields on a repair (admin)",
        "description": "Admin corrects submitter free-text fields (`part_to_repair`, `repair_cost_approx`, `repair_shop_name`, `work_order`, `notes`). Does not change `status` or `closed_at` — use close / status-updates for those. Omitted fields are left unchanged. Returns the updated repair.\n",
        "operationId": "updateRepairs",
        "tags": [
          "site-admin-only"
        ],
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "Unique identifier of the repair (short id).",
            "schema": {
              "$ref": "#/components/schemas/repair_properties-id"
            }
          }
        ],
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "additionalProperties": false,
                "properties": {
                  "part_to_repair": {
                    "$ref": "#/components/schemas/part_to_repair"
                  },
                  "repair_cost_approx": {
                    "$ref": "#/components/schemas/repair_cost_approx"
                  },
                  "repair_shop_name": {
                    "$ref": "#/components/schemas/repair_shop_name"
                  },
                  "work_order": {
                    "$ref": "#/components/schemas/work_order"
                  },
                  "notes": {
                    "$ref": "#/components/schemas/notes"
                  }
                }
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Repair updated successfully.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "repair"
                  ],
                  "properties": {
                    "repair": {
                      "$ref": "#/components/schemas/repair"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Repair not found.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/repairs/{id}/status-updates": {
      "post": {
        "summary": "Post a status update on a repair (admin)",
        "description": "Admin appends an append-only status update to a repair, bumps the repair's `updated_at`, and notifies the submitter. Returns the new status update and the updated repair.\n",
        "operationId": "statusUpdatesRepairs",
        "tags": [
          "site-admin-only"
        ],
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "Unique identifier of the repair (short id).",
            "schema": {
              "$ref": "#/components/schemas/repair_properties-id"
            }
          }
        ],
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "additionalProperties": false,
                "required": [
                  "body"
                ],
                "properties": {
                  "body": {
                    "$ref": "#/components/schemas/body"
                  }
                }
              }
            }
          }
        },
        "responses": {
          "201": {
            "description": "Status update created; repair returned with updated timestamp.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "repairStatusUpdate",
                    "repair"
                  ],
                  "properties": {
                    "repairStatusUpdate": {
                      "$ref": "#/components/schemas/repair-status-update"
                    },
                    "repair": {
                      "$ref": "#/components/schemas/repair"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Repair not found.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/repairs/{id}/close": {
      "post": {
        "summary": "Close a repair (admin)",
        "description": "Admin closes an open repair, setting `status` to `closed` and `closed_at`. Optional `body` is stored as a closing status-update note. Returns the updated repair. Fails with 409 if the repair is already closed.\n",
        "operationId": "closeRepairs",
        "tags": [
          "site-admin-only"
        ],
        "parameters": [
          {
            "name": "id",
            "in": "path",
            "required": true,
            "description": "Unique identifier of the repair (short id).",
            "schema": {
              "$ref": "#/components/schemas/repair_properties-id"
            }
          }
        ],
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "additionalProperties": false,
                "properties": {
                  "body": {
                    "$ref": "#/components/schemas/body"
                  }
                }
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Repair closed successfully.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "repair"
                  ],
                  "properties": {
                    "repair": {
                      "$ref": "#/components/schemas/repair"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "404": {
            "description": "Repair not found.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "409": {
            "description": "Conflict - repair is already closed.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    },
    "/mercury-transaction-allocations/by-repair-ids": {
      "get": {
        "summary": "Batch fetch allocations by repair IDs",
        "description": "Admin batch lookup of Mercury transaction allocations for many repairs (detail UIs). Returns allocations grouped by repair id. Each requested id is present in the map (empty array when that repair has no allocations).\n",
        "operationId": "byRepairIdsMercuryTransactionAllocations",
        "tags": [
          "site-admin-only"
        ],
        "parameters": [
          {
            "name": "repairIds",
            "in": "query",
            "required": true,
            "description": "Comma-separated repair ids to fetch allocations for.\n",
            "style": "form",
            "explode": false,
            "schema": {
              "type": "array",
              "items": {
                "$ref": "#/components/schemas/repair_properties-id"
              }
            }
          }
        ],
        "responses": {
          "200": {
            "description": "Allocations keyed by repair id. Every requested id is a key; values are empty arrays when there are no allocations.\n",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "additionalProperties": false,
                  "required": [
                    "mercuryTransactionAllocationsByRepairId"
                  ],
                  "properties": {
                    "mercuryTransactionAllocationsByRepairId": {
                      "type": "object",
                      "description": "Map from repair id to that repair's allocations.\n",
                      "additionalProperties": {
                        "type": "array",
                        "items": {
                          "$ref": "#/components/schemas/mercury-transaction-allocation"
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "description": "Unauthorized - missing or invalid auth headers, or not logged in.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "403": {
            "description": "Forbidden - site admin privileges required.",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          },
          "422": {
            "description": "Unprocessable entity — one or more `repairIds` do not reference an existing repair.\n",
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/error"
                }
              }
            }
          }
        }
      }
    }
  },
  "components": {
    "schemas": {
      "ProductEvent": {
        "$ref": "#/components/schemas/index"
      },
      "Error": {
        "$ref": "#/components/schemas/error"
      },
      "Address": {
        "$ref": "#/components/schemas/address"
      },
      "Organization": {
        "$ref": "#/components/schemas/organization"
      },
      "OrganizationRequest": {
        "$ref": "#/components/schemas/organization-request"
      },
      "Part": {
        "$ref": "#/components/schemas/part"
      },
      "PartFile": {
        "$ref": "#/components/schemas/part-file"
      },
      "PartInterestRequest": {
        "$ref": "#/components/schemas/part-interest-request"
      },
      "PartInterestResponse": {
        "$ref": "#/components/schemas/part-interest-response"
      },
      "PartRequest": {
        "$ref": "#/components/schemas/part-request"
      },
      "PartSource": {
        "$ref": "#/components/schemas/part-source"
      },
      "PartType": {
        "$ref": "#/components/schemas/part-type"
      },
      "SellInterestRequest": {
        "$ref": "#/components/schemas/sell-interest-request"
      },
      "SellInterestResponse": {
        "$ref": "#/components/schemas/sell-interest-response"
      },
      "ContactRequest": {
        "$ref": "#/components/schemas/contact-request"
      },
      "SupportFeedbackRequest": {
        "$ref": "#/components/schemas/support-feedback-request"
      },
      "User": {
        "$ref": "#/components/schemas/user"
      },
      "MercuryTransaction": {
        "$ref": "#/components/schemas/mercury-transaction"
      },
      "MercuryTransactionAllocation": {
        "$ref": "#/components/schemas/mercury-transaction-allocation"
      },
      "OrgLedgerSummary": {
        "$ref": "#/components/schemas/org-ledger-summary"
      },
      "OrgLedgerEntry": {
        "$ref": "#/components/schemas/org-ledger-entry"
      },
      "AdminDashboardSummary": {
        "$ref": "#/components/schemas/admin-dashboard-summary"
      },
      "Repair": {
        "$ref": "#/components/schemas/repair"
      },
      "RepairStatusUpdate": {
        "$ref": "#/components/schemas/repair-status-update"
      },
      "part-type": {
        "type": "string",
        "enum": [
          "Actuator",
          "Adapter",
          "Adaptor",
          "Alternator",
          "Assembly",
          "Barrel",
          "Base",
          "Basket",
          "Baskets/Liners",
          "Bearing",
          "Bellows",
          "Belt",
          "Blade",
          "Body",
          "Bolt",
          "Boss",
          "Bracket",
          "Breather",
          "Bucket",
          "Buckets/Blades",
          "Bulb",
          "Bushing",
          "C-Ring",
          "Cabinet",
          "Cable",
          "Can",
          "Cap",
          "Carbide",
          "Card",
          "Cartridge",
          "Casing",
          "Clamp",
          "Clip",
          "Coating",
          "Coil",
          "Collar",
          "Compressor",
          "Cone",
          "Connector",
          "Controller",
          "Cooler",
          "Core",
          "Coupling",
          "Cup",
          "Cylinder",
          "D-Key",
          "Deflector",
          "Detector",
          "Diaphragm",
          "Diode",
          "Disc",
          "Dogbone",
          "Dowel",
          "Elbow",
          "Element",
          "End Cap",
          "Exciter",
          "Exhaust",
          "Filter",
          "Fitting",
          "Fixture",
          "Flange",
          "Flow Sleeve",
          "Frame",
          "Fuel Nozzles",
          "Gasket",
          "Gauge",
          "Gearbox",
          "Gland",
          "Glass",
          "Guard",
          "Handle",
          "Helicoil",
          "Housing",
          "Housing",
          "Hula",
          "Indicator",
          "Insert",
          "Insulation",
          "Insulator",
          "Key",
          "Kit",
          "Klozure",
          "Latch",
          "Liner",
          "Lock",
          "Lockplate",
          "Lubricator",
          "Manifold",
          "Metering",
          "Module",
          "Nozzle",
          "Nozzles/Vanes",
          "Nut",
          "O-Ring",
          "Orifice",
          "Packing",
          "Peg",
          "Pick-up",
          "Pilot",
          "Pin",
          "Pipe",
          "Piston",
          "Piston Ring",
          "Plate",
          "Plug",
          "Powder",
          "Preform",
          "Proximitor",
          "Pump",
          "Reducer",
          "Regulator",
          "Relay",
          "Resistor",
          "Retainer",
          "Ring",
          "Rivet",
          "Rod",
          "Rotor",
          "Rupture",
          "Sample",
          "Screen",
          "Screw",
          "Seal",
          "Segment",
          "Sensor",
          "Shaft",
          "Shim",
          "Shroud Blocks/Blade Rings",
          "Skid",
          "Sleeve",
          "Solenoid",
          "Spacer",
          "Spring",
          "Stator",
          "Stop",
          "Strainer",
          "Strut",
          "Stud",
          "Swirler",
          "Switch",
          "Thermocouple",
          "Threadolet",
          "Tip",
          "Tooling",
          "Torque",
          "Transition Piece",
          "Trapezoid",
          "Tube",
          "Tubing",
          "Turbine",
          "Union",
          "Valve",
          "Vane",
          "Wash",
          "Washer",
          "Weight",
          "Wheel",
          "Winding",
          "Zipper",
          "Other",
          null
        ],
        "nullable": true,
        "description": "Type of the part based on its function and characteristics",
        "example": "Buckets/Blades"
      },
      "address": {
        "type": "object",
        "description": "A shared address type that can be used across the platform",
        "properties": {
          "formatted": {
            "type": [
              "string",
              "null"
            ],
            "description": "Complete formatted address string",
            "example": "123 Main St, Anytown, ST 12345, USA"
          },
          "street_address": {
            "type": [
              "string",
              "null"
            ],
            "description": "Street address line",
            "example": "123 Main St"
          },
          "locality": {
            "type": [
              "string",
              "null"
            ],
            "description": "City or town name",
            "example": "Anytown"
          },
          "region": {
            "type": [
              "string",
              "null"
            ],
            "description": "State, province, or region",
            "example": "ST"
          },
          "country": {
            "type": [
              "string",
              "null"
            ],
            "description": "Country name",
            "example": "USA"
          },
          "postal_code": {
            "type": [
              "string",
              "null"
            ],
            "description": "ZIP or postal code",
            "example": "12345"
          }
        },
        "example": {
          "formatted": "123 Main St, Anytown, ST 12345, USA",
          "street_address": "123 Main St",
          "locality": "Anytown",
          "region": "ST",
          "country": "USA",
          "postal_code": "12345"
        }
      },
      "part": {
        "type": "object",
        "description": "A marketplace listing for a physical turbine part. Combines part identity and listing fields (status, pricing, organization assignment, delist metadata).\n",
        "properties": {
          "id": {
            "type": "string",
            "description": "Unique identifier for the part (short id generated server-side; URL-safe base64url string).\n",
            "minLength": 1,
            "example": "xY3nK9pL"
          },
          "description": {
            "type": "string",
            "nullable": true,
            "description": "Optional free-form description shown to buyers.",
            "example": "Turbine blade assembly in serviceable condition."
          },
          "manufacturer": {
            "type": "string",
            "description": "Manufacturer name for the part.",
            "minLength": 1,
            "maxLength": 500,
            "example": "General Electric"
          },
          "frame": {
            "type": "string",
            "description": "Frame identifier or designation for the part.",
            "minLength": 1,
            "maxLength": 500,
            "example": "Frame-001"
          },
          "part_number": {
            "type": "string",
            "description": "Manufacturer or seller part number.",
            "minLength": 1,
            "maxLength": 500,
            "example": "PN-12345"
          },
          "turbine_model": {
            "type": "string",
            "description": "Turbine model this part is intended for.",
            "minLength": 1,
            "maxLength": 500,
            "example": "GE-1.5MW"
          },
          "serial_number": {
            "type": "string",
            "nullable": true,
            "description": "Optional serial number when available.",
            "example": "SN-ABC123456789"
          },
          "upgrades": {
            "type": "string",
            "nullable": true,
            "description": "Optional notes on upgrades or modifications.",
            "example": "Stage 5 bucket upgrade kit installed."
          },
          "casting_number": {
            "type": "string",
            "nullable": true,
            "description": "Optional casting or foundry number.",
            "example": "C-88421"
          },
          "quantity": {
            "type": "integer",
            "description": "Quantity available for this listing.",
            "minimum": 1,
            "maximum": 999999999,
            "example": 10
          },
          "price_currency": {
            "type": "string",
            "enum": [
              "USD"
            ],
            "description": "ISO currency code for the unit price; only USD is supported today.",
            "example": "USD"
          },
          "price_unit_amount": {
            "type": "integer",
            "nullable": true,
            "description": "Unit price in the smallest currency unit (e.g. US cents for USD).",
            "minimum": 0,
            "example": 15000
          },
          "matched_hardware": {
            "type": "boolean",
            "nullable": true,
            "description": "Whether matched hardware is included or applicable.",
            "example": true
          },
          "total_factored_fired_hours": {
            "type": "integer",
            "nullable": true,
            "description": "Optional total factored fired hours from service history.",
            "minimum": 0,
            "example": 1500
          },
          "total_factored_fired_starts": {
            "type": "integer",
            "nullable": true,
            "description": "Optional total factored fired starts from service history.",
            "minimum": 0,
            "example": 500
          },
          "emergency_trips": {
            "type": "integer",
            "nullable": true,
            "description": "Optional count of emergency trips from service history.",
            "minimum": 0,
            "example": 25
          },
          "times_refurbished": {
            "type": "integer",
            "nullable": true,
            "description": "Optional number of refurbishment cycles.",
            "minimum": 0,
            "example": 3
          },
          "status": {
            "type": "string",
            "enum": [
              "available",
              "pending",
              "sold",
              "removed",
              null
            ],
            "nullable": true,
            "description": "Listing lifecycle status. \"removed\" means delisted by the owning organization. Null may appear for legacy or incomplete records.\n",
            "example": "available"
          },
          "condition": {
            "type": "string",
            "enum": [
              "new",
              "repairable",
              "repaired",
              "as removed",
              "inspected",
              "scrap",
              null
            ],
            "nullable": true,
            "description": "Physical condition of the part.",
            "example": "new"
          },
          "part_type": {
            "$ref": "#/components/schemas/part-type"
          },
          "date_listed": {
            "type": "string",
            "nullable": true,
            "description": "When the part was listed (date or ISO 8601 timestamp string).",
            "example": "2023-12-15T10:30:00Z"
          },
          "date_sold": {
            "type": "string",
            "nullable": true,
            "description": "When the part was marked sold (date or ISO 8601 timestamp string).",
            "example": "2023-12-20T14:45:00Z"
          },
          "tags": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "nullable": true,
            "description": "Arbitrary string tags for search or internal grouping.",
            "example": [
              "urgent",
              "maintenance",
              "priority"
            ]
          },
          "address": {
            "$ref": "#/components/schemas/address",
            "type": "object",
            "nullable": true,
            "description": "Optional pickup or ship-from address for the listing."
          },
          "file_summary": {
            "type": "object",
            "nullable": true,
            "description": "Counts of uploaded files by category (pedigree, photos, reports, etc.). Keys correspond to part file categories; values are non-negative integers.\n",
            "additionalProperties": {
              "type": "integer",
              "minimum": 0
            }
          },
          "thumbnail_url": {
            "type": "string",
            "nullable": true,
            "description": "URL of the selected thumbnail image derived from uploaded photos.",
            "example": "https://example.com/thumbnails/part-123-thumb.jpg"
          },
          "source_id": {
            "type": "string",
            "nullable": true,
            "description": "When populated, references the part_source row this part was created from (CSV import batch).\n",
            "example": "a1b2c3d4-e5f6-7890-abcd-ef1234567890"
          },
          "org_id": {
            "type": "string",
            "nullable": true,
            "description": "Owning organization identifier when the part is assigned to an organization (nullable string referencing `Organization.id`). Null when unassigned. Some endpoints omit this field for non-admin callers. Values use the same short-id format as organizations, not OpenAPI `format: uuid`.\n",
            "minLength": 1,
            "example": "org_m4nQ8sRt"
          },
          "sold_off_platform": {
            "type": "boolean",
            "default": false,
            "description": "True when the seller indicated the part was sold outside the marketplace (e.g. after delist).\n",
            "example": false
          },
          "raw_data": {
            "type": "object",
            "nullable": true,
            "description": "Original import row as JSON (typically admin-only).",
            "additionalProperties": true,
            "example": {
              "column1": "value1",
              "column2": "value2",
              "notes": "additional data"
            }
          },
          "parsed_data": {
            "type": "object",
            "nullable": true,
            "description": "Structured fields parsed from the import row (typically admin-only).",
            "additionalProperties": true,
            "example": {
              "manufacturer": "GE",
              "part_number": "PN-12345"
            }
          },
          "created_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the part record was created (ISO 8601).",
            "example": "2023-01-01T00:00:00.000Z"
          },
          "updated_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the part record was last updated (ISO 8601).",
            "example": "2023-01-02T12:00:00.000Z"
          }
        },
        "required": [
          "id",
          "manufacturer",
          "frame",
          "part_number",
          "turbine_model",
          "quantity",
          "price_currency",
          "sold_off_platform",
          "created_at",
          "updated_at"
        ]
      },
      "social-url": {
        "type": "object",
        "description": "A social media URL entry that can be expanded for future fields",
        "properties": {
          "url": {
            "type": "string",
            "description": "The social media URL",
            "format": "uri",
            "example": "https://twitter.com/acme"
          }
        },
        "required": [
          "url"
        ]
      },
      "organization": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "description": "Unique identifier for the organization (short id, not a UUID)",
            "example": "a1b2c3d4e5f6g7h8"
          },
          "creator_id": {
            "type": "string",
            "description": "User identifier of the organization creator",
            "example": "user_123456789"
          },
          "name": {
            "type": "string",
            "description": "Company name",
            "example": "Acme Corporation",
            "minLength": 1,
            "maxLength": 255
          },
          "dba": {
            "type": "string",
            "description": "Doing business as name",
            "example": "Acme Corp",
            "minLength": 1,
            "maxLength": 255,
            "nullable": true
          },
          "email": {
            "type": "string",
            "description": "Company email address",
            "example": "contact@acme.com",
            "format": "email",
            "nullable": true
          },
          "phone": {
            "type": "string",
            "description": "Company phone number",
            "example": "+1-555-123-4567",
            "pattern": "^\\+?[1-9]\\d{1,14}$",
            "nullable": true
          },
          "website": {
            "type": "string",
            "description": "Company website URL",
            "example": "https://www.acme.com",
            "format": "uri",
            "nullable": true
          },
          "about": {
            "type": "string",
            "description": "Company description or about information",
            "example": "A leading technology company specializing in innovative solutions",
            "nullable": true
          },
          "year_founded": {
            "type": "integer",
            "description": "Year the company was founded",
            "example": 2020,
            "minimum": 1800,
            "maximum": 2100,
            "nullable": true
          },
          "address": {
            "$ref": "#/components/schemas/address",
            "type": "object",
            "nullable": true
          },
          "social_urls": {
            "type": "array",
            "description": "Array of social media URLs for the company",
            "items": {
              "$ref": "#/components/schemas/social-url"
            },
            "nullable": true,
            "example": [
              {
                "url": "https://twitter.com/acme"
              },
              {
                "url": "https://linkedin.com/company/acme"
              },
              {
                "url": "https://facebook.com/acme"
              }
            ]
          },
          "logo_url": {
            "type": "string",
            "description": "URL to the company logo image",
            "example": "https://example.com/logos/acme-logo.png",
            "format": "uri",
            "nullable": true
          },
          "intends_to_sell_parts": {
            "type": "boolean",
            "description": "Whether the organization intends to sell or list parts on PAX",
            "default": false
          },
          "intends_to_buy_parts": {
            "type": "boolean",
            "description": "Whether the organization intends to buy parts on PAX",
            "default": false
          },
          "created_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the organization was created",
            "example": "2023-01-01T00:00:00Z"
          },
          "updated_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the organization was last updated",
            "example": "2023-01-01T00:00:00Z"
          }
        },
        "required": [
          "id",
          "creator_id",
          "name",
          "created_at",
          "updated_at"
        ]
      },
      "error": {
        "type": "object",
        "properties": {
          "code": {
            "type": "string",
            "description": "A short, machine-readable error code, for when HTTP status codes are not sufficient."
          },
          "message": {
            "type": "string",
            "description": "A human-readable description of the error.",
            "example": "The requested resource could not be found."
          }
        }
      },
      "part-request": {
        "type": "object",
        "description": "Fields accepted when creating or updating a part (admin). Omitted properties are left unchanged on update where applicable.\n",
        "properties": {
          "description": {
            "type": "string",
            "nullable": true,
            "description": "Optional description of the part.",
            "example": "Turbine blade assembly"
          },
          "manufacturer": {
            "type": "string",
            "description": "Manufacturer of the part.",
            "minLength": 1,
            "maxLength": 500,
            "example": "General Electric"
          },
          "frame": {
            "type": "string",
            "description": "Frame identifier for the part.",
            "minLength": 1,
            "maxLength": 500,
            "example": "Frame-001"
          },
          "part_number": {
            "type": "string",
            "description": "Part number identifier.",
            "minLength": 1,
            "maxLength": 500,
            "example": "PN-12345"
          },
          "turbine_model": {
            "type": "string",
            "description": "Model of the turbine this part is designed for.",
            "minLength": 1,
            "maxLength": 500,
            "example": "GE-1.5MW"
          },
          "serial_number": {
            "type": "string",
            "nullable": true,
            "description": "Optional serial number.",
            "example": "SN-ABC123456789"
          },
          "upgrades": {
            "type": "string",
            "nullable": true,
            "description": "Optional upgrades or modification notes."
          },
          "casting_number": {
            "type": "string",
            "nullable": true,
            "description": "Optional casting number."
          },
          "quantity": {
            "type": "integer",
            "description": "Quantity of the part.",
            "minimum": 1,
            "maximum": 999999999
          },
          "price_currency": {
            "type": "string",
            "enum": [
              "USD"
            ],
            "description": "Currency code for pricing.",
            "example": "USD"
          },
          "price_unit_amount": {
            "type": "integer",
            "nullable": true,
            "description": "Optional unit price in smallest currency unit (e.g. cents).",
            "minimum": 0,
            "example": 150
          },
          "matched_hardware": {
            "type": "boolean",
            "nullable": true,
            "description": "Optional flag for matched hardware.",
            "example": true
          },
          "total_factored_fired_hours": {
            "type": "integer",
            "nullable": true,
            "description": "Optional total factored fired hours.",
            "minimum": 0,
            "example": 1500
          },
          "total_factored_fired_starts": {
            "type": "integer",
            "nullable": true,
            "description": "Optional total factored fired starts.",
            "minimum": 0,
            "example": 500
          },
          "emergency_trips": {
            "type": "integer",
            "nullable": true,
            "description": "Optional emergency trips count.",
            "minimum": 0,
            "example": 25
          },
          "times_refurbished": {
            "type": "integer",
            "nullable": true,
            "description": "Optional refurbishment count.",
            "minimum": 0,
            "example": 3
          },
          "status": {
            "type": "string",
            "enum": [
              "available",
              "pending",
              "sold",
              "removed",
              null
            ],
            "nullable": true,
            "description": "Optional listing status.",
            "example": "available"
          },
          "condition": {
            "type": "string",
            "enum": [
              "new",
              "repairable",
              "repaired",
              "as removed",
              "inspected",
              "scrap",
              null
            ],
            "nullable": true,
            "description": "Optional physical condition.",
            "example": "new"
          },
          "part_type": {
            "$ref": "#/components/schemas/part-type"
          },
          "date_listed": {
            "type": "string",
            "nullable": true,
            "description": "Optional date or ISO 8601 timestamp when the part was listed.",
            "example": "2023-12-15T10:30:00Z"
          },
          "date_sold": {
            "type": "string",
            "nullable": true,
            "description": "Optional date or ISO 8601 timestamp when the part was sold.",
            "example": "2023-12-20T14:45:00Z"
          },
          "tags": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "nullable": true,
            "description": "Optional list of string tags.",
            "example": [
              "urgent",
              "maintenance",
              "priority"
            ]
          },
          "address": {
            "$ref": "#/components/schemas/address",
            "type": "object",
            "nullable": true,
            "description": "Optional address for the listing."
          },
          "thumbnail_url": {
            "type": "string",
            "nullable": true,
            "description": "Optional thumbnail URL.",
            "example": "https://example.com/thumbnails/part-123-thumb.jpg"
          },
          "org_id": {
            "type": "string",
            "nullable": true,
            "description": "Optional owning organization id (same string format as `Organization.id`). Set to assign or reassign the part; null clears assignment when the API supports explicit null. Must reference an existing organization when set.\n",
            "minLength": 1,
            "example": "org_m4nQ8sRt"
          },
          "sold_off_platform": {
            "type": "boolean",
            "description": "Whether the part was sold outside the marketplace. Optional on write; defaults to false when omitted on create.\n",
            "example": false
          }
        },
        "required": [
          "manufacturer",
          "frame",
          "part_number",
          "turbine_model",
          "quantity",
          "price_currency"
        ]
      },
      "category": {
        "type": "string",
        "enum": [
          "pedigree",
          "shop_release",
          "flow_test_report",
          "dimensional_inspection",
          "nde_report",
          "photo"
        ],
        "description": "Category classification of the file",
        "example": "pedigree"
      },
      "part-file": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "format": "uuid",
            "description": "Unique identifier for the part file",
            "example": "123e4567-e89b-12d3-a456-426614174000"
          },
          "part_id": {
            "type": "string",
            "description": "Reference to the part this file belongs to",
            "example": "PART-001-2024"
          },
          "category": {
            "type": "string",
            "enum": [
              "pedigree",
              "shop_release",
              "flow_test_report",
              "dimensional_inspection",
              "nde_report",
              "photo"
            ],
            "description": "Category classification of the file",
            "example": "pedigree"
          },
          "url": {
            "type": "string",
            "description": "URL for the file in the Azure blob storage system"
          },
          "file_original_name": {
            "type": "string",
            "description": "Original filename when uploaded",
            "example": "part_certificate_2024.pdf"
          },
          "mimetype": {
            "type": "string",
            "description": "MIME type of the file",
            "example": "application/pdf"
          },
          "size": {
            "type": "integer",
            "minimum": 0,
            "description": "File size in bytes",
            "example": 1048576
          },
          "created_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the part file was created",
            "example": "2024-01-15T10:30:00Z"
          },
          "updated_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the part file was last updated",
            "example": "2024-01-15T10:30:00Z"
          }
        },
        "required": [
          "id",
          "part_id",
          "category",
          "file_original_name",
          "mimetype",
          "size",
          "created_at",
          "updated_at"
        ]
      },
      "organization-request": {
        "type": "object",
        "properties": {
          "name": {
            "type": "string",
            "description": "Company name",
            "example": "Acme Corporation",
            "minLength": 1,
            "maxLength": 255
          },
          "dba": {
            "type": "string",
            "description": "Doing business as name",
            "example": "Acme Corp",
            "minLength": 1,
            "maxLength": 255,
            "nullable": true
          },
          "email": {
            "type": "string",
            "description": "Company email address",
            "example": "contact@acme.com",
            "format": "email",
            "nullable": true
          },
          "phone": {
            "type": "string",
            "description": "Company phone number",
            "example": "+1-555-123-4567",
            "pattern": "^\\+?[1-9]\\d{1,14}$",
            "nullable": true
          },
          "website": {
            "type": "string",
            "description": "Company website URL",
            "example": "https://www.acme.com",
            "format": "uri",
            "nullable": true
          },
          "about": {
            "type": "string",
            "description": "Company description or about information",
            "example": "A leading technology company specializing in innovative solutions",
            "nullable": true
          },
          "year_founded": {
            "type": "integer",
            "description": "Year the company was founded",
            "example": 2020,
            "minimum": 1800,
            "maximum": 2100,
            "nullable": true
          },
          "address": {
            "$ref": "#/components/schemas/address",
            "type": "object",
            "nullable": true
          },
          "social_urls": {
            "type": "array",
            "description": "Array of social media URLs for the company",
            "items": {
              "$ref": "#/components/schemas/social-url"
            },
            "nullable": true,
            "example": [
              {
                "url": "https://twitter.com/acme"
              },
              {
                "url": "https://linkedin.com/company/acme"
              },
              {
                "url": "https://facebook.com/acme"
              }
            ]
          },
          "logo_url": {
            "type": "string",
            "description": "URL to the company logo image",
            "example": "https://example.com/logos/acme-logo.png",
            "format": "uri",
            "nullable": true
          },
          "intends_to_sell_parts": {
            "type": "boolean",
            "description": "Whether the organization intends to sell or list parts on PAX"
          },
          "intends_to_buy_parts": {
            "type": "boolean",
            "description": "Whether the organization intends to buy parts on PAX"
          }
        },
        "required": [
          "name"
        ]
      },
      "user": {
        "type": "object",
        "description": "Lightweight user representation resolved from Kratos identity data (e.g. for admin organization listings). Not a persisted application entity.\n",
        "properties": {
          "id": {
            "type": "string",
            "description": "Kratos identity identifier for this user (matches `creator_id` on organizations they created). Stored and transmitted as an opaque string (short id style from the identity provider, not a UUID).\n",
            "example": "user_7f3a9b2c1d4e5f6"
          },
          "email": {
            "type": "string",
            "format": "email",
            "description": "Primary email address from the user's Kratos identity.",
            "example": "jane.seller@example.com"
          },
          "name": {
            "type": "object",
            "nullable": true,
            "description": "Given and family name from the user's Kratos identity profile. Null when no name traits are set.\n",
            "properties": {
              "first": {
                "type": "string",
                "description": "Given name.",
                "example": "Jane"
              },
              "last": {
                "type": "string",
                "description": "Family name.",
                "example": "Seller"
              }
            },
            "required": [
              "first",
              "last"
            ],
            "example": {
              "first": "Jane",
              "last": "Seller"
            }
          },
          "phone": {
            "type": "string",
            "nullable": true,
            "description": "Phone number from the user's Kratos identity profile, when present.",
            "example": "+1-555-987-6543"
          }
        },
        "required": [
          "id",
          "email"
        ]
      },
      "part-interest-request": {
        "type": "object",
        "properties": {
          "partId": {
            "type": "string",
            "minLength": 1,
            "description": "Identifier of the part the user is interested in (short id)",
            "example": "xY3nK9pL"
          },
          "interestedFiles": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "description": "Array of file category keys the user is interested in",
            "example": [
              "pedigree",
              "shop_release",
              "flow_test_report",
              "dimensional_inspection",
              "nde_report",
              "photo"
            ]
          },
          "message": {
            "type": "string",
            "description": "User's message expressing interest in the part",
            "example": "I'm interested in this part for a new project. Could you provide more details about availability?",
            "maxLength": 2000
          }
        },
        "required": [
          "partId",
          "interestedFiles",
          "message"
        ]
      },
      "part-interest-response": {
        "type": "object",
        "properties": {
          "partId": {
            "type": "string",
            "minLength": 1,
            "description": "Identifier of the part the user expressed interest in (short id)",
            "example": "xY3nK9pL"
          },
          "interestedFiles": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "description": "Array of file category keys the user requested"
          },
          "message": {
            "type": "string",
            "description": "Success message confirming the interest request was received",
            "example": "Your interest in this part has been recorded. We'll contact you within 24 hours with the requested files."
          }
        },
        "required": [
          "partId",
          "interestedFiles",
          "message"
        ]
      },
      "sell-interest-request": {
        "type": "object",
        "description": "Request to sell turbine part(s); contact email and phone come from the authenticated session.",
        "properties": {
          "companyName": {
            "type": "string",
            "description": "Legal or trading company name"
          },
          "dba": {
            "type": "string",
            "nullable": true,
            "description": "Doing business as name"
          },
          "address": {
            "$ref": "#/components/schemas/address"
          },
          "contactName": {
            "type": "string",
            "description": "Primary contact name for this request"
          },
          "partDescription": {
            "type": "string",
            "description": "Description of the part(s) the user wants to sell",
            "maxLength": 8000
          }
        },
        "required": [
          "companyName",
          "address",
          "contactName",
          "partDescription"
        ]
      },
      "sell-interest-response": {
        "type": "object",
        "properties": {
          "message": {
            "type": "string",
            "description": "Confirmation message"
          }
        },
        "required": [
          "message"
        ]
      },
      "contact-request": {
        "type": "object",
        "description": "Public contact form submission (unauthenticated).",
        "properties": {
          "name": {
            "type": "string",
            "minLength": 1,
            "maxLength": 200,
            "description": "Contact name",
            "example": "Jane Buyer"
          },
          "email": {
            "type": "string",
            "format": "email",
            "description": "Reply-to email address",
            "example": "jane@example.com"
          },
          "phone": {
            "type": "string",
            "maxLength": 50,
            "description": "Phone number (optional)",
            "example": "+1 555 0100"
          },
          "company": {
            "type": "string",
            "maxLength": 200,
            "description": "Company or organization (optional)",
            "example": "Acme Turbines"
          },
          "message": {
            "type": "string",
            "minLength": 1,
            "maxLength": 5000,
            "description": "Message body",
            "example": "I would like to learn more about your marketplace."
          }
        },
        "required": [
          "name",
          "email",
          "message"
        ]
      },
      "support-feedback-request": {
        "type": "object",
        "additionalProperties": false,
        "description": "Authenticated account user submits support feedback. Staff receive an email using the provided subject line.\n",
        "required": [
          "feedback_type",
          "subject",
          "message"
        ],
        "properties": {
          "feedback_type": {
            "type": "string",
            "minLength": 1,
            "maxLength": 200,
            "description": "Category or label for the kind of feedback (e.g. UI selection sent by the client)."
          },
          "subject": {
            "type": "string",
            "minLength": 1,
            "maxLength": 300,
            "description": "Subject line used for the notification email to staff."
          },
          "message": {
            "type": "string",
            "minLength": 1,
            "maxLength": 5000,
            "description": "Full message body from the user."
          }
        }
      },
      "part-source": {
        "type": "object",
        "properties": {
          "id": {
            "type": "string",
            "format": "uuid",
            "description": "Unique identifier for the part source",
            "example": "123e4567-e89b-12d3-a456-426614174000"
          },
          "kind": {
            "type": "string",
            "enum": [
              "csv"
            ],
            "description": "Type of part source",
            "example": "csv"
          },
          "blob_name": {
            "type": "string",
            "description": "Azure blob name where the file is stored",
            "example": "part-sources/2024/01/15/parts-import-123.csv"
          },
          "file_original_name": {
            "type": "string",
            "description": "Original filename when uploaded",
            "example": "parts-import.csv"
          },
          "mimetype": {
            "type": "string",
            "description": "MIME type of the uploaded file",
            "example": "text/csv"
          },
          "size": {
            "type": "integer",
            "minimum": 0,
            "description": "File size in bytes",
            "example": 1024
          },
          "uploaded_by": {
            "type": "string",
            "description": "User ID who uploaded the file",
            "example": "user-123"
          },
          "rows_count": {
            "type": "integer",
            "minimum": 0,
            "description": "Number of rows imported from this source",
            "example": 150
          },
          "created_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the part source was created",
            "example": "2024-01-15T10:30:00Z"
          },
          "updated_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the part source was last updated",
            "example": "2024-01-15T10:30:00Z"
          }
        },
        "required": [
          "id",
          "kind",
          "blob_name",
          "file_original_name",
          "mimetype",
          "size",
          "uploaded_by",
          "rows_count",
          "created_at",
          "updated_at"
        ]
      },
      "admin-dashboard-summary": {
        "type": "object",
        "description": "Counts for the admin home dashboard. Outstanding amounts are unallocated remainders across mirrored Mercury transactions (`parent.amount_cents - allocated_amount_cents`). Includes open repair request counts for ops.\n",
        "properties": {
          "outstanding_transaction_count": {
            "type": "integer",
            "minimum": 0,
            "description": "Number of Mercury transactions that are outstanding: no allocations, or the sum of allocation amounts does not equal the parent `amount_cents`.\n",
            "example": 3
          },
          "outstanding_amount_cents": {
            "type": "integer",
            "description": "Sum of unallocated remainders across outstanding transactions (`parent.amount_cents - allocated_amount_cents`), in the smallest currency unit (e.g. US cents).\n",
            "example": 14500
          },
          "open_repair_count": {
            "type": "integer",
            "minimum": 0,
            "description": "Number of repair requests with `status = open`.\n",
            "example": 2
          }
        },
        "required": [
          "outstanding_transaction_count",
          "outstanding_amount_cents",
          "open_repair_count"
        ]
      },
      "outstanding": {
        "type": "boolean",
        "description": "True when the transaction has no allocations, or the sum of allocation amounts does not equal `amount_cents` (incomplete split).\n",
        "example": true
      },
      "posted_at": {
        "type": "string",
        "format": "date-time",
        "description": "When Mercury posted the transaction (ISO 8601).",
        "example": "2026-09-15T14:30:00.000Z"
      },
      "mercury-transaction": {
        "type": "object",
        "description": "Mirrored Mercury bank transaction (local ledger row). Allocations are not nested; return them as sibling resources. `allocated_amount_cents` and `outstanding` are computed from current allocations. `raw` is admin-only and may be omitted from non-admin responses (users do not receive this object in v1).\n",
        "properties": {
          "id": {
            "type": "string",
            "description": "Local unique identifier (short id from generateShortId).",
            "minLength": 1,
            "example": "K3m9_xR2"
          },
          "mercury_id": {
            "type": "string",
            "description": "Mercury's transaction identifier (UUID string from Mercury; not a local short id).",
            "minLength": 1,
            "example": "a1b2c3d4-e5f6-7890-abcd-ef1234567890"
          },
          "mercury_account_id": {
            "type": "string",
            "description": "Mercury account the transaction belongs to.",
            "minLength": 1,
            "example": "f0e1d2c3-b4a5-6789-0123-456789abcdef"
          },
          "amount_cents": {
            "type": "integer",
            "description": "Signed amount in the smallest currency unit (e.g. US cents). Positive = funds into our Mercury account; negative = funds out.\n",
            "example": 10000
          },
          "currency": {
            "type": "string",
            "description": "ISO currency code for the amount.",
            "example": "USD"
          },
          "posted_at": {
            "type": "string",
            "format": "date-time",
            "description": "When Mercury posted the transaction (ISO 8601).",
            "example": "2026-09-15T14:30:00.000Z"
          },
          "created_at_mercury": {
            "type": [
              "string",
              "null"
            ],
            "format": "date-time",
            "description": "Mercury `createdAt` when present; null if Mercury did not provide it.",
            "example": "2026-09-15T14:29:55.000Z"
          },
          "status": {
            "type": "string",
            "description": "Mercury transaction status string (e.g. pending, sent).",
            "minLength": 1,
            "example": "sent"
          },
          "counterparty_name": {
            "type": [
              "string",
              "null"
            ],
            "description": "Best-effort display name for the counterparty from the Mercury payload.",
            "example": "Acme Corporation"
          },
          "bank_description": {
            "type": [
              "string",
              "null"
            ],
            "description": "Mercury description or note fields useful for admin reconciliation.",
            "example": "Wire from Acme — invoice 4421"
          },
          "first_seen_at": {
            "type": "string",
            "format": "date-time",
            "description": "When this row was first inserted by sync (ISO 8601).",
            "example": "2026-09-15T14:35:00.000Z"
          },
          "last_synced_at": {
            "type": "string",
            "format": "date-time",
            "description": "When this row was last upserted by a successful sync (ISO 8601).",
            "example": "2026-09-21T10:00:00.000Z"
          },
          "allocated_amount_cents": {
            "type": "integer",
            "description": "Sum of current allocation `amount_cents` for this transaction (computed). Zero when there are no allocations.\n",
            "example": 7000
          },
          "outstanding": {
            "type": "boolean",
            "description": "True when the transaction has no allocations, or the sum of allocation amounts does not equal `amount_cents` (incomplete split).\n",
            "example": true
          },
          "raw": {
            "type": "object",
            "additionalProperties": true,
            "description": "Full Mercury transaction payload for audit and future fields. Admin-only; omit from any user-facing response.\n",
            "example": {
              "id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
              "amount": 100,
              "status": "sent"
            }
          }
        },
        "required": [
          "id",
          "mercury_id",
          "mercury_account_id",
          "amount_cents",
          "currency",
          "posted_at",
          "status",
          "first_seen_at",
          "last_synced_at",
          "allocated_amount_cents",
          "outstanding"
        ]
      },
      "id": {
        "type": "string",
        "description": "Local unique identifier (short id from generateShortId).",
        "minLength": 1,
        "example": "K3m9_xR2"
      },
      "mercury-transaction-allocation": {
        "type": "object",
        "description": "Tagging / split row for a mirrored Mercury bank transaction. A parent transaction may have zero or more allocations. When fully tagged, allocation amounts sum to the parent's `amount_cents` (same sign). Partial tagging is allowed while work is in progress.\n",
        "properties": {
          "id": {
            "type": "string",
            "description": "Unique identifier for the allocation (short id from generateShortId).",
            "minLength": 1,
            "example": "K3m9_xR2"
          },
          "mercury_transaction_id": {
            "type": "string",
            "description": "Local id of the parent Mercury transaction this allocation splits.",
            "minLength": 1,
            "example": "Tx7nQ2pL"
          },
          "org_id": {
            "type": [
              "string",
              "null"
            ],
            "description": "Selling organization credited or debited by this allocation. Required for `part_sale`, `withdrawal`, and `repair_spend`; null for `platform_cut` and `test`. Short id referencing `Organization.id` (not OpenAPI `format: uuid`).\n",
            "example": "org_m4nQ8sRt"
          },
          "kind": {
            "type": "string",
            "enum": [
              "part_sale",
              "platform_cut",
              "withdrawal",
              "repair_spend",
              "test"
            ],
            "description": "How this slice of the parent transaction is classified. `part_sale` credits a selling org for a sold part; `platform_cut` is the marketplace fee; `withdrawal` is a seller cashing out; `repair_spend` debits an org for funds applied to a repair request (`org_id` and `repair_id` required); `test` tags a smoke-test deposit or the matching transfer out and is excluded from seller balances (`org_id`, `part_id`, and `repair_id` null).\n",
            "example": "part_sale"
          },
          "part_id": {
            "type": [
              "string",
              "null"
            ],
            "description": "Part listing this allocation is for. Required when `kind` is `part_sale`; otherwise null. Short id referencing `Part.id`.\n",
            "example": "xY3nK9pL"
          },
          "repair_id": {
            "type": [
              "string",
              "null"
            ],
            "description": "Repair this allocation applies funds to. Required when `kind` is `repair_spend`; otherwise null. Short id referencing `Repair.id`.\n",
            "example": "Rp7nK2mQ"
          },
          "amount_cents": {
            "type": "integer",
            "description": "Signed amount in the smallest currency unit (e.g. US cents). Must be non-zero and share the same sign as the parent transaction's `amount_cents`.\n",
            "example": 7000
          },
          "note": {
            "type": [
              "string",
              "null"
            ],
            "description": "Optional admin free-text note for reconciliation.",
            "example": "Split: seller share after 10% platform cut"
          },
          "tagged_by_user_id": {
            "type": "string",
            "description": "Kratos identity id of the admin who last wrote this row.",
            "minLength": 1,
            "example": "user_a1b2c3d4"
          },
          "created_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the allocation was created (ISO 8601).",
            "example": "2026-09-15T15:00:00.000Z"
          },
          "updated_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the allocation was last updated (ISO 8601).",
            "example": "2026-09-21T10:00:00.000Z"
          }
        },
        "required": [
          "id",
          "mercury_transaction_id",
          "kind",
          "amount_cents",
          "tagged_by_user_id",
          "created_at",
          "updated_at"
        ]
      },
      "mercury_transaction_id": {
        "type": "string",
        "description": "Local id of the parent Mercury transaction this allocation splits.",
        "minLength": 1,
        "example": "Tx7nQ2pL"
      },
      "org_id": {
        "type": [
          "string",
          "null"
        ],
        "description": "Selling organization credited or debited by this allocation. Required for `part_sale`, `withdrawal`, and `repair_spend`; null for `platform_cut` and `test`. Short id referencing `Organization.id` (not OpenAPI `format: uuid`).\n",
        "example": "org_m4nQ8sRt"
      },
      "kind": {
        "type": "string",
        "enum": [
          "part_sale",
          "platform_cut",
          "withdrawal",
          "repair_spend",
          "test"
        ],
        "description": "How this slice of the parent transaction is classified. `part_sale` credits a selling org for a sold part; `platform_cut` is the marketplace fee; `withdrawal` is a seller cashing out; `repair_spend` debits an org for funds applied to a repair request (`org_id` and `repair_id` required); `test` tags a smoke-test deposit or the matching transfer out and is excluded from seller balances (`org_id`, `part_id`, and `repair_id` null).\n",
        "example": "part_sale"
      },
      "part_id": {
        "type": [
          "string",
          "null"
        ],
        "description": "Part listing this allocation is for. Required when `kind` is `part_sale`; otherwise null. Short id referencing `Part.id`.\n",
        "example": "xY3nK9pL"
      },
      "repair_id": {
        "type": [
          "string",
          "null"
        ],
        "description": "Repair this allocation applies funds to. Required when `kind` is `repair_spend`; otherwise null. Short id referencing `Repair.id`.\n",
        "example": "Rp7nK2mQ"
      },
      "amount_cents": {
        "type": "integer",
        "description": "Signed amount in the smallest currency unit (e.g. US cents). Must be non-zero and share the same sign as the parent transaction's `amount_cents`.\n",
        "example": 7000
      },
      "note": {
        "type": [
          "string",
          "null"
        ],
        "description": "Optional admin free-text note for reconciliation.",
        "example": "Split: seller share after 10% platform cut"
      },
      "properties-id": {
        "type": "string",
        "description": "Unique identifier for the allocation (short id from generateShortId).",
        "minLength": 1,
        "example": "K3m9_xR2"
      },
      "org-ledger-summary": {
        "type": "object",
        "description": "User-facing ledger balance for an organization. `balance_cents` is the sum of allocation `amount_cents` where `org_id` matches and `kind` is `part_sale`, `withdrawal`, or `repair_spend`; `platform_cut` and `test` are excluded.\n",
        "properties": {
          "org_id": {
            "type": "string",
            "description": "Organization this summary belongs to. Short id referencing `Organization.id` (not OpenAPI `format: uuid`).\n",
            "minLength": 1,
            "example": "org_m4nQ8sRt"
          },
          "balance_cents": {
            "type": "integer",
            "description": "Signed balance in the smallest currency unit (e.g. US cents). Positive means the org is owed funds; negative means the org owes / has withdrawn more than credited.\n",
            "example": 25000
          },
          "currency": {
            "type": "string",
            "description": "ISO currency code for the balance. Only USD is supported today.",
            "example": "USD"
          },
          "as_of": {
            "type": "string",
            "format": "date-time",
            "description": "Server time when this summary was computed (ISO 8601). Useful on printed statements.\n",
            "example": "2026-09-21T18:00:00.000Z"
          }
        },
        "required": [
          "org_id",
          "balance_cents",
          "currency",
          "as_of"
        ]
      },
      "org-ledger-entry": {
        "type": "object",
        "description": "Flattened org credit activity row for the Credit page and printable statement. Derived from an allocation plus its parent Mercury transaction (and optional part). Related `Part` / `Organization` objects are returned as sibling arrays when needed, not nested.\n",
        "properties": {
          "allocation_id": {
            "type": "string",
            "description": "Id of the underlying `MercuryTransactionAllocation` this row is derived from (short id from generateShortId; not OpenAPI `format: uuid`).\n",
            "minLength": 1,
            "example": "K3m9_xR2"
          },
          "mercury_transaction_id": {
            "type": "string",
            "description": "Local id of the parent Mercury transaction. Short id referencing `MercuryTransaction.id`.\n",
            "minLength": 1,
            "example": "Tx7nQ2pL"
          },
          "kind": {
            "type": "string",
            "enum": [
              "part_sale",
              "platform_cut",
              "withdrawal",
              "repair_spend",
              "test"
            ],
            "description": "Allocation classification. User-facing ledger balances include `part_sale`, `withdrawal`, and `repair_spend`; `platform_cut` and `test` are excluded from org balance.\n",
            "example": "part_sale"
          },
          "amount_cents": {
            "type": "integer",
            "description": "Signed amount in the smallest currency unit (e.g. US cents) for this entry, taken from the allocation.\n",
            "example": 7000
          },
          "posted_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the parent Mercury transaction was posted (ISO 8601). Used for chronological ordering on statements.\n",
            "example": "2026-09-15T14:30:00.000Z"
          },
          "part_id": {
            "type": [
              "string",
              "null"
            ],
            "description": "Part listing this entry is for when `kind` is `part_sale`; otherwise null. Short id referencing `Part.id`.\n",
            "example": "xY3nK9pL"
          },
          "note": {
            "type": [
              "string",
              "null"
            ],
            "description": "Optional free-text note from the underlying allocation.",
            "example": "Split: seller share after 10% platform cut"
          },
          "running_balance_cents": {
            "type": "integer",
            "description": "Org balance in cents after this entry when entries are ordered oldest→newest. Populated on full statement responses; may be omitted on the compact Credit activity feed when cheaper to compute client-side.\n",
            "example": 25000
          }
        },
        "required": [
          "allocation_id",
          "mercury_transaction_id",
          "kind",
          "amount_cents",
          "posted_at"
        ]
      },
      "status": {
        "type": "string",
        "enum": [
          "open",
          "closed"
        ],
        "description": "Lifecycle status. Only `open` and `closed` in v1; progress detail lives in status-update posts.\n",
        "example": "open"
      },
      "repair": {
        "type": "object",
        "description": "Repair credit request tracked for a seller organization. Created when a user submits a repair request; admins post status updates and close the repair. Linked from `repair_spend` Mercury allocations when funds are applied.\n",
        "properties": {
          "id": {
            "type": "string",
            "description": "Unique identifier for the repair (short id from generateShortId).",
            "minLength": 1,
            "example": "Rp7nK2mQ"
          },
          "org_id": {
            "type": "string",
            "description": "Owning seller organization (short id referencing `Organization.id`).\n",
            "minLength": 1,
            "example": "org_m4nQ8sRt"
          },
          "created_by_user_id": {
            "type": "string",
            "description": "Kratos identity of the user who submitted the repair request (opaque string, not OpenAPI `format: uuid`).\n",
            "minLength": 1,
            "example": "user_7f3a9b2c1d4e5f6"
          },
          "status": {
            "type": "string",
            "enum": [
              "open",
              "closed"
            ],
            "description": "Lifecycle status. Only `open` and `closed` in v1; progress detail lives in status-update posts.\n",
            "example": "open"
          },
          "part_to_repair": {
            "type": "string",
            "description": "Free-text description of the part to be repaired.",
            "minLength": 1,
            "example": "Stage 1 nozzle — PN-4421, SN-ABC123"
          },
          "repair_cost_approx": {
            "type": "string",
            "description": "Approximate repair cost as free text (v1; not structured cents/currency).\n",
            "minLength": 1,
            "example": "$1,200"
          },
          "repair_shop_name": {
            "type": "string",
            "description": "Name of the repair shop performing the work.",
            "minLength": 1,
            "example": "Acme Turbine Repair"
          },
          "work_order": {
            "type": "string",
            "description": "Shop work-order or reference number.",
            "minLength": 1,
            "example": "WO-2026-0915"
          },
          "notes": {
            "type": [
              "string",
              "null"
            ],
            "description": "Optional submitter notes; null when none were provided.",
            "example": "Blade tips show wear; photos sent separately."
          },
          "created_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the repair was created (ISO 8601).",
            "example": "2026-09-15T14:30:00.000Z"
          },
          "updated_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the repair was last updated (ISO 8601).",
            "example": "2026-09-16T10:00:00.000Z"
          },
          "closed_at": {
            "type": [
              "string",
              "null"
            ],
            "format": "date-time",
            "description": "When the repair was closed (ISO 8601). Null while `status` is `open`; set when status becomes `closed`.\n",
            "example": "2026-09-20T16:45:00.000Z"
          }
        },
        "required": [
          "id",
          "org_id",
          "created_by_user_id",
          "status",
          "part_to_repair",
          "repair_cost_approx",
          "repair_shop_name",
          "work_order",
          "created_at",
          "updated_at"
        ]
      },
      "repair_properties-id": {
        "type": "string",
        "description": "Unique identifier for the repair (short id from generateShortId).",
        "minLength": 1,
        "example": "Rp7nK2mQ"
      },
      "repair-status-update": {
        "type": "object",
        "description": "Append-only status post on a repair. Authored by admins in v1; shown to the repair's submitter chronologically.\n",
        "properties": {
          "id": {
            "type": "string",
            "description": "Unique identifier for the status update (short id from generateShortId).",
            "minLength": 1,
            "example": "su_K3m9xR2q"
          },
          "repair_id": {
            "type": "string",
            "description": "Parent repair (short id referencing `Repair.id`).\n",
            "minLength": 1,
            "example": "Rp7nK2mQ"
          },
          "body": {
            "type": "string",
            "description": "Status message shown to the user.",
            "minLength": 1,
            "example": "Parts ordered from the OEM; ETA 2 weeks."
          },
          "author_user_id": {
            "type": "string",
            "description": "Kratos identity of the admin who authored the update (opaque string, not OpenAPI `format: uuid`).\n",
            "minLength": 1,
            "example": "user_7f3a9b2c1d4e5f6"
          },
          "created_at": {
            "type": "string",
            "format": "date-time",
            "description": "When the status update was created (ISO 8601).",
            "example": "2026-09-16T10:00:00.000Z"
          }
        },
        "required": [
          "id",
          "repair_id",
          "body",
          "author_user_id",
          "created_at"
        ]
      },
      "part_to_repair": {
        "type": "string",
        "description": "Free-text description of the part to be repaired.",
        "minLength": 1,
        "example": "Stage 1 nozzle — PN-4421, SN-ABC123"
      },
      "repair_cost_approx": {
        "type": "string",
        "description": "Approximate repair cost as free text (v1; not structured cents/currency).\n",
        "minLength": 1,
        "example": "$1,200"
      },
      "repair_shop_name": {
        "type": "string",
        "description": "Name of the repair shop performing the work.",
        "minLength": 1,
        "example": "Acme Turbine Repair"
      },
      "work_order": {
        "type": "string",
        "description": "Shop work-order or reference number.",
        "minLength": 1,
        "example": "WO-2026-0915"
      },
      "notes": {
        "type": [
          "string",
          "null"
        ],
        "description": "Optional submitter notes; null when none were provided.",
        "example": "Blade tips show wear; photos sent separately."
      },
      "body": {
        "type": "string",
        "description": "Status message shown to the user.",
        "minLength": 1,
        "example": "Parts ordered from the OEM; ETA 2 weeks."
      },
      "login": {
        "type": "object",
        "properties": {
          "event": {
            "type": "string",
            "enum": [
              "login"
            ]
          },
          "context": {
            "type": "object",
            "properties": {
              "method": {
                "type": "string",
                "enum": [
                  "email"
                ]
              }
            }
          }
        },
        "required": [
          "event",
          "context"
        ]
      },
      "signup": {
        "type": "object",
        "properties": {
          "event": {
            "type": "string",
            "enum": [
              "signup"
            ]
          },
          "context": {
            "type": "object",
            "properties": {
              "method": {
                "type": "string",
                "enum": [
                  "email"
                ]
              }
            }
          }
        }
      },
      "signup_view": {
        "type": "object",
        "properties": {
          "event": {
            "type": "string",
            "enum": [
              "signup_view"
            ]
          }
        }
      },
      "verify_email": {
        "type": "object",
        "properties": {
          "event": {
            "type": "string",
            "enum": [
              "verify_email"
            ]
          }
        }
      },
      "part_trace": {
        "type": "object",
        "properties": {
          "manufacturer": {
            "type": "string"
          },
          "turbine_model": {
            "type": "string"
          },
          "frame": {
            "type": "string"
          },
          "casting_number": {
            "type": "string"
          },
          "part_number": {
            "type": "string"
          },
          "part_id": {
            "type": "string"
          }
        }
      },
      "contact_form_submit": {
        "type": "object",
        "properties": {
          "event": {
            "type": "string",
            "enum": [
              "contact_form_submit"
            ]
          },
          "context": {
            "$ref": "#/components/schemas/part_trace"
          }
        },
        "required": [
          "event",
          "context"
        ]
      },
      "contact_form_view": {
        "type": "object",
        "properties": {
          "event": {
            "type": "string",
            "enum": [
              "contact_form_view"
            ]
          },
          "context": {
            "$ref": "#/components/schemas/part_trace"
          }
        },
        "required": [
          "event",
          "context"
        ]
      },
      "part_detail_view": {
        "type": "object",
        "properties": {
          "event": {
            "type": "string",
            "enum": [
              "part_detail_view"
            ]
          },
          "context": {
            "$ref": "#/components/schemas/part_trace"
          }
        },
        "required": [
          "event",
          "context"
        ]
      },
      "index": {
        "type": "object",
        "oneOf": [
          {
            "$ref": "#/components/schemas/login"
          },
          {
            "$ref": "#/components/schemas/signup"
          },
          {
            "$ref": "#/components/schemas/signup_view"
          },
          {
            "$ref": "#/components/schemas/verify_email"
          },
          {
            "$ref": "#/components/schemas/contact_form_submit"
          },
          {
            "$ref": "#/components/schemas/contact_form_view"
          },
          {
            "$ref": "#/components/schemas/part_detail_view"
          }
        ],
        "properties": {
          "event": {
            "type": "string"
          },
          "client": {
            "type": "string",
            "description": "The frontend client that triggered the event. For web, it should be \"web-{spa-name}\"."
          },
          "view": {
            "type": "string",
            "description": "The page that triggered the event. For vue, it should be the route name provided by vue router."
          },
          "component": {
            "type": "string",
            "description": "The component that triggered the event. For vue, it should be the component name."
          }
        },
        "required": [
          "event"
        ]
      }
    }
  }
}
